import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { theme } from "../../utils/global-styles/theme";
import { Link } from "gatsby";
import CheckMarkIcon from "../CourseCard/Icons/CheckMarkIcon";
import dayjs from "../../utils/dayjsConfig";

const CourseDetailHero = ({
  courseImage,
  hubspotClassName,
  title,
  provider,
  providerUrl,
  courseType,
  shortDesc,
  courseTheme,
  themeNumber,
  ctaText,
  courseCost,
  courseDuration,
  courseAvailability,
  onEnrollmentClick,
  enrollmentStatus,
  courseStartDate,
  headerCourseStartDate,
  onGoToCourseClick,
  viewCourseCatalogLink,
  viewCourseCatalogText,
  sso,
  redirectUrl,
  validatingToken,
  flexiblePayment,
  setShowFlexiblePaymentModal,
  newTheme,
}) => {
  const [hasCourseStarted, setHasCourseStarted] = useState(false);

  useEffect(() => {
    if (enrollmentStatus === "enrolled" && courseStartDate) {
      const currentDate = dayjs(new Date())
          .utcOffset("-0700")
          .format("YYYY-MM-DD"),
        startDate = dayjs(courseStartDate);

      setHasCourseStarted(Number(startDate.diff(currentDate, "days")) <= 0);
    }
  }, [hasCourseStarted, enrollmentStatus, courseStartDate]);

  const HeroContainer = styled.section`
      height: auto;
      width: 100%;
      display: flex;
      padding: 33px 40px;
      flex-direction: column;
      position: relative;

      @media only screen and (min-width: ${theme.breakpoints.desktopUp}) {
        height: max-content;
        padding: 50px 80px;
      }
    `,
    HeaderCourseTypeContainer = styled.div`
      display: flex;
      flex-direction: column;
      z-index: 1;
    `,
    HeaderCourseType = styled.h3`
      font-size: 17px;
      font-weight: 500;
      letter-spacing: -0.22px;
      line-height: 30px;
      margin-bottom: 30px;

      @media only screen and (min-width: ${theme.breakpoints.desktopUp}) {
        margin-bottom: 35px;
      }
    `,
    ViewCourseCatalogLink = styled(Link)`
      display: inline-block;
      text-decoration: underline;
      text-underline-offset: 5px;
      text-decoration-thickness: 3px;
      height: 1.65em;
      font-size: 12px;
      font-weight: bold;
      letter-spacing: 0.34px;
      line-height: 14px;
      margin-bottom: 35px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        height: 1.5em;
        font-size: 12px;
        font-weight: bold;
        line-height: 18px;
        margin-bottom: 30px;
      }
    `,
    MainInfoContainer = styled.div`
      z-index: 1;
      max-width: 100%;

      @media only screen and (min-width: ${theme.breakpoints.desktopUp}) {
        max-width: 80%;
      }
    `,
    Title = styled.h1`
      font-weight: bold;
      letter-spacing: -1.38px;
      margin-bottom: 35px;
      font-size: 70px;
      line-height: 75px;

      @media only screen and (max-width: ${theme.breakpoints.desktopUp}) {
        margin-bottom: 35px;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-weight: bold;
        letter-spacing: -1.92px;
        font-size: 35px;
        line-height: 40px;
        margin-bottom: 30px;
      }
    `,
    ShortDesc = styled.h3`
      font-size: 35px;
      font-weight: bold;
      letter-spacing: -0.91px;
      line-height: 42px;
      width: 100%;
      margin-bottom: 35px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 25px;
        font-weight: bold;
        letter-spacing: -0.52px;
        line-height: 30px;
        margin-bottom: 30px;
      }

      @media only screen and (min-width: ${theme.breakpoints.desktopUp}) {
        font-size: 35px;
        line-height: 40px;
        margin-bottom: 35px;
      }
    `,
    CourseInfoCtaSubGrid = styled.div`
      font-family: ${theme.fontFaces.headers};
      z-index: 1;
    `,
    BottomInfoContainer = styled.div`
      display: flex;
      margin-bottom: 35px;

      @media only screen and (max-width: ${theme.breakpoints.desktopUp}) {
        display: block;
        padding-bottom: 0px;
        margin-bottom: 0px;
      }
    `,
    CourseInfoContainer = styled.div`
      display: flex;
    `,
    CourseInfoText = styled.div`
      font-size: 0.9em;
      font-weight: bold;
      line-height: 1.25em;
      border-top: 4px solid;
      margin-right: 1.5em;
      padding-top: 5px;
      text-transform: capitalize;
      align-self: center;

      @media only screen and (max-width: ${theme.breakpoints.desktopUp}) {
        width: max-content;
        margin-bottom: 30px;
      }
      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 0.85em;
      }
    `,
    CtaButtonContainer = styled.div`
      align-self: center;
    `,
    CtaButton = styled.button``,
    StartDateText = styled.p`
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.37px;
      line-height: 16px;
      text-align: center;
      font-family: ${theme.fontFaces.headers};
      margin-top: 7px;

      @media only screen and (max-width: ${theme.breakpoints.desktopUp}) {
        text-align: left;
        margin-left: 28px;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        text-align: center;
        margin-left: 0;
      }
    `,
    HeroImage = styled(GatsbyImage)`
      position: absolute !important;
      top: 0;
      overflow: hidden;
      right: 0;
      left: 0;
      bottom: 0;

      @media only screen and (max-width: ${theme.breakpoints.desktopUp}) {
        height: 100%;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        height: 100%;
      }
    `,
    Content = styled.div`
      display: flex;
      flex-direction: column;
    `,
    determineCtaContainerContent = () => {
      if (enrollmentStatus === "enrolled") {
        return hasCourseStarted ||
          courseAvailability.toLowerCase().trim() === "on demand" ? (
          <CtaButton
            className={`black-button ${
              validatingToken ? "disabled-button" : ""
            }`}
            onClick={() =>
              validatingToken
                ? {}
                : sso
                  ? window.location.replace(redirectUrl)
                  : onGoToCourseClick(provider, providerUrl)
            }
          >
            Go to Course
          </CtaButton>
        ) : (
          <>
            <CtaButton className="disabled-button">
              <CheckMarkIcon height="24px" width="24px" marginBottom="4px" />
              &nbsp;Enrolled
            </CtaButton>
            {courseStartDate && (
              <StartDateText>Course starts {courseStartDate}</StartDateText>
            )}
          </>
        );
      } else {
        return (
          <CtaButton
            data-testId="enroll-now"
            className={`${hubspotClassName} black-button ${
              validatingToken ? "disabled-button" : ""
            }`}
            onClick={() =>
              flexiblePayment
                ? setShowFlexiblePaymentModal(true)
                : validatingToken
                  ? {}
                  : sso
                    ? window.location.replace(redirectUrl)
                    : onEnrollmentClick()
            }
          >
            {ctaText}
          </CtaButton>
        );
      }
    };
  let formattedCourseStartDate;
  if (!headerCourseStartDate) {
    formattedCourseStartDate = "On-Demand";
  } else {
    if (headerCourseStartDate) {
      const formattedDate = dayjs(headerCourseStartDate).format("MMM D, YYYY");

      formattedCourseStartDate = formattedDate;
    }
  }
  const image = getImage(courseImage.gatsbyImageData);
  return (
    <HeroContainer id="header">
      {image && <HeroImage image={image} alt={courseImage.description} />}
      <Content>
        <HeaderCourseTypeContainer>
          <ViewCourseCatalogLink
            to={`${viewCourseCatalogLink}/`}
            className="main secondary-border-hover"
          >
            {viewCourseCatalogText}
          </ViewCourseCatalogLink>
          <HeaderCourseType>{newTheme[0]?.title}</HeaderCourseType>
        </HeaderCourseTypeContainer>
        <MainInfoContainer>
          <Title>{title}</Title>
          <ShortDesc>{shortDesc}</ShortDesc>
        </MainInfoContainer>
        <CourseInfoCtaSubGrid>
          <BottomInfoContainer>
            <CourseInfoContainer>
              <CourseInfoText>{courseDuration}</CourseInfoText>
              <CourseInfoText>{formattedCourseStartDate}</CourseInfoText>
              <CourseInfoText>{courseCost}</CourseInfoText>
            </CourseInfoContainer>
          </BottomInfoContainer>
          <CtaButtonContainer>
            {determineCtaContainerContent()}
          </CtaButtonContainer>
        </CourseInfoCtaSubGrid>
      </Content>
    </HeroContainer>
  );
};

CourseDetailHero.propTypes = {
  courseImage: PropTypes.shape({
    fluid: PropTypes.shape({
      srcSet: PropTypes.string,
    }),
    description: PropTypes.string,
  }),
};

export default CourseDetailHero;
